import { useMemo, useState, Dispatch, SetStateAction } from 'react'
import { debounce } from 'lodash'
import { useCallbackRef } from '@web/walter-ui-kit'

/**
 * useDebouncedState
 */
export default function <T>(initialState: T | (() => T), timeout = 300) {
    const [state, setState] = useState(initialState)
    const [isWaiting, setIsWaiting] = useState(false)

    const debouncedSetState = useMemo(
        () =>
            debounce((value: T) => {
                setState(value)
                setIsWaiting(false)
            }, timeout),
        [timeout]
    )

    const debounced = useCallbackRef((value: T) => {
        !isWaiting && setIsWaiting(true)
        debouncedSetState(value)
    })

    const setImmediately: Dispatch<SetStateAction<T>> = useCallbackRef(next => {
        setState(next)
        setIsWaiting(false)
        debouncedSetState.cancel()
    })

    return [state, debounced, setImmediately, isWaiting] as const
}
